import React, { useEffect, useMemo, useRef } from 'react';

import { graphql, useLazyLoadQuery } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { type ApiError } from '@atlassian/conversation-assistant-service-api/utils/api-error';
import { useRecommendedSpaces } from '@atlassian/rovo-spaces/services/useRecommendedSpaces';

import type { RecommendedSpacesPanelQuery as RecommendedSpacesPanelQueryType } from './__generated__/RecommendedSpacesPanelQuery.graphql';
import { RecommendedSpacesPanelRenderer } from './RecommendedSpacesPanelRenderer';

const ROVO_AGENTS_FE_TEAM_NAME = 'rovo-agents-fe';
const HIPAA_ERROR_CODE = 'convoai.rest.browsing.product.has.hipaa.enabled';

// A HIPAA-disabled product returns an expected error, not a real failure — treat
// it as a successful (empty) load so it doesn't pollute the failure SLO. Mirrors
// the handling in usePrefetchRecommendedSpacesOnOpenIntent.
const isHipaaError = (error: unknown): boolean =>
	typeof error === 'object' &&
	error !== null &&
	(error as ApiError).errorAttributes?.errorCode === HIPAA_ERROR_CODE;

export interface RecommendedSpacesPanelProps {
	cloudId: string;
	userId?: string;
	onCreateSpace: (
		spaceTitle: string,
		sources: Array<{
			ari: string;
			url: string;
			sourceType: string;
			conversationIds: string[];
			name?: string;
		}>,
	) => void;
	chatRefreshHeader?: React.ReactNode;
}

/**
 * GraphQL-based data fetcher (legacy path, used when `rovo_space_recommended` gate is off).
 */
const RecommendedSpacesPanelGraphQL = ({
	cloudId,
	userId,
	onCreateSpace,
	chatRefreshHeader,
}: RecommendedSpacesPanelProps) => {
	const data = useLazyLoadQuery<RecommendedSpacesPanelQueryType>(
		/* eslint-disable @atlassian/relay/unused-fields -- fields consumed by extractSourcesFromThread */
		graphql`
			query RecommendedSpacesPanelQuery(
				$cloudId: ID!
				$experience: String!
				$homeThreadsInput: ConvoAiHomeThreadsInput!
			) {
				convoai_homeThreads(
					cloudId: $cloudId
					experience: $experience
					homeThreadsInput: $homeThreadsInput
				) {
					edges {
						node {
							id
							title
							sources {
								__typename
								... on ConvoAiHomeThreadsFirstPartySource {
									ari
									url
									data {
										__typename
									}
								}
								... on ConvoAiHomeThreadsThirdPartySource {
									url
								}
							}
						}
					}
				}
			}
		`,
		/* eslint-enable @atlassian/relay/unused-fields */
		{
			cloudId,
			experience: 'home-threads',
			homeThreadsInput: { userId: userId ?? '' },
		},
	);

	const threads = useMemo(() => {
		const edges = data?.convoai_homeThreads?.edges ?? [];
		return edges
			.map((edge) => edge?.node)
			.filter((node) => node?.id != null && node?.title != null)
			.map((node) => ({
				id: node!.id!,
				title: node!.title!,
				sources: node!.sources ?? null,
			}));
	}, [data]);

	return (
		<RecommendedSpacesPanelRenderer
			threads={threads}
			onCreateSpace={onCreateSpace}
			chatRefreshHeader={chatRefreshHeader}
		/>
	);
};

/**
 * REST-based data fetcher (new path, used when `rovo_space_recommended` gate is on).
 * Calls `GET /api/rovo/v1/spaces/recommended` which returns threads with
 * server-side filtering of threads already converted to spaces.
 */
const RecommendedSpacesPanelREST = ({
	cloudId,
	onCreateSpace,
	chatRefreshHeader,
}: RecommendedSpacesPanelProps) => {
	const { threads, isLoading, error } = useRecommendedSpaces({
		cloudId,
		product: 'rovo',
		experienceId: 'ai-mate',
	});

	// End the perceived-load experience started at click (see rovo-navigation's
	// handleSelectRecommendedSpacesClick). Succeed once loading settles — which is
	// immediate when the ROVOFE-3252 prefetch has already warmed the cache — so the
	// recorded duration reflects perceived click→render time. `succeed`/`fail`/`abort`
	// are no-ops when no experience is active (e.g. the panel shown without a click).
	const experienceTracker = useAIMateExperienceTracker();
	const settledRef = useRef(false);
	useEffect(() => {
		if (settledRef.current) {
			return;
		}
		if (error) {
			settledRef.current = true;
			if (isHipaaError(error)) {
				experienceTracker.succeed({ name: ExperienceName.OPEN_RECOMMENDED_SPACES });
			} else {
				experienceTracker.fail({
					name: ExperienceName.OPEN_RECOMMENDED_SPACES,
					error,
					teamName: ROVO_AGENTS_FE_TEAM_NAME,
				});
			}
			return;
		}
		if (!isLoading) {
			settledRef.current = true;
			experienceTracker.succeed({ name: ExperienceName.OPEN_RECOMMENDED_SPACES });
		}
	}, [isLoading, error, experienceTracker]);

	// If the user leaves before the panel settles, abandon the experience so it
	// isn't left dangling (and doesn't skew the perceived-load timing).
	useEffect(
		() => () => {
			if (!settledRef.current) {
				experienceTracker.abort({
					name: ExperienceName.OPEN_RECOMMENDED_SPACES,
					reason: 'Recommended spaces panel closed before load settled',
				});
			}
		},
		[experienceTracker],
	);

	return (
		<RecommendedSpacesPanelRenderer
			threads={threads}
			onCreateSpace={onCreateSpace}
			chatRefreshHeader={chatRefreshHeader}
			isLoading={isLoading}
		/>
	);
};

export const RecommendedSpacesPanel = (
	props: RecommendedSpacesPanelProps,
): React.JSX.Element | null => {
	if (!props.userId) {
		return null;
	}
	if (fg('rovo_space_recommended')) {
		return <RecommendedSpacesPanelREST {...props} />;
	}
	return <RecommendedSpacesPanelGraphQL {...props} />;
};
